import { MagnifyingGlassIcon, ArrowClockwiseIcon } from "@phosphor-icons/react";
import { Button } from "./components/ui/button";
import { createContext, useContext, useEffect, useRef, useState } from "react";

// All loading regions on a route share one start time, including the account read.
export const LoadingStarted = createContext<number | null>(null);
function usePlaceholderVisible() {
  const ownStart = useRef(Date.now());
  const started = useContext(LoadingStarted) ?? ownStart.current;
  const [readyFor, setReadyFor] = useState<number | null>(null);
  const visible = readyFor === started || Date.now() - started >= 150;
  useEffect(() => {
    if (visible) return;
    const timer = setTimeout(() => setReadyFor(started), Math.max(0, 150 - (Date.now() - started)));
    return () => clearTimeout(timer);
  }, [started, visible]);
  return visible;
}
/** Placeholder lines. A label is also the region's text, so a reader of the page text (not only a screen reader) knows it is loading. */
export function LoadingLines({ rows = 3, label, className = "" }: { rows?: number; label?: string; className?: string }) {
  const visible = usePlaceholderVisible();
  return <div className={`loading-lines ${className}`} role={label ? "status" : undefined} aria-busy="true" data-visible={visible}>
    {label && <span className="sr-only">{label}…</span>}
    {Array.from({ length: rows }, (_, i) => <span className="loading-line-slot" key={i} aria-hidden="true"><span className="loading-mark" /></span>)}
  </div>;
}
export function LoadingRows({ columns, rows = 8 }: { columns: number; rows?: number }) {
  const visible = usePlaceholderVisible();
  return <tbody className="loading-rows" aria-busy="true" aria-label="Loading rows" data-visible={visible}>
    {Array.from({ length: rows }, (_, row) => <tr key={row}>{Array.from({ length: columns }, (_, col) => <td key={col}><span className="loading-mark" aria-hidden="true" /></td>)}</tr>)}
  </tbody>;
}
export function ReaderLoading({ run = false }: { run?: boolean }) {
  return <>
    {run && <div className="run-toolbar-loading"><LoadingLines rows={2} label="Loading run" /></div>}
    <div className={`reader-loading ${run ? "reader-loading-run" : ""}`}>
    <LoadingLines className="reader-loading-title" rows={1} label={run ? undefined : "Loading method"} />
    <LoadingLines className="reader-loading-actions" rows={1} />
    <LoadingLines rows={6} />
  </div></>;
}
export function PageLoading({ page }: { page: string }) {
  if (page === "detail" || page === "run") return <ReaderLoading run={page === "run"} />;
  if (page !== "workflows" && page !== "runs") return <LoadingLines className="reader-loading" rows={4} label="Loading page" />;
  const runs = page === "runs";
  return <section className="p-wrap" aria-busy="true">
    <div className="p-bar">
      <label className="list-search"><MagnifyingGlassIcon size={16} /><input disabled placeholder={runs ? "Find a run by name, input or result…" : "Find a method…"} aria-label="Loading search" /></label>
      <Button variant="ghost" size="icon" className="ml-auto" disabled aria-label="Loading refresh"><ArrowClockwiseIcon size={17} /></Button>
    </div>
    {runs
      ? <table className="runs-panel"><thead><tr className="r-row r-head">{["Run", "Inputs", "Result", "Time", "Started", ""].map((label, index) => <th key={index} scope="col">{label}</th>)}</tr></thead><LoadingRows columns={6} /></table>
      : <div><div className="m-row m-head"><span>Method · last result</span><span>Rhythm</span><span>Last 10 runs</span><span /></div><LoadingLines rows={8} label="Loading methods" /></div>}
  </section>;
}

import "./loading.css";
